import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user, profile } = useAuth();
  const { itemCount, openCart } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Shop', path: '/shop' },
    { name: 'About', path: '/about' },
    { name: 'How to Order', path: '/how-to-order' },
    { name: 'Quality', path: '/quality' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/f774deb3335ef344be2f4793f6ebd557/d3524caf242f33efd937ef8bb569492a.png"
              alt="DN Bio Research Logo"
              className="h-11 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap cursor-pointer ${
                  location.pathname === link.path
                    ? 'text-primary-600 bg-primary-50'
                    : isScrolled
                    ? 'text-navy-700 hover:text-primary-600 hover:bg-gray-50'
                    : 'text-white hover:text-primary-300 hover:bg-white/10'
                }`}
              >
                {link.name}
              </Link>
            ))}

            {/* Cart Icon */}
            <button
              onClick={openCart}
              className="relative text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
              aria-label="Shopping cart"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-shopping-cart-line text-2xl"></i>
              </div>
              {itemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-teal-600 text-white text-xs font-semibold w-5 h-5 flex items-center justify-center rounded-full">
                  {itemCount > 99 ? '99+' : itemCount}
                </span>
              )}
            </button>

            {user ? (
              <Link
                to="/account"
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  isScrolled
                    ? 'text-gray-700 hover:text-teal-600'
                    : 'text-white hover:text-teal-200'
                }`}
              >
                <i className="ri-user-line text-lg"></i>
                <span className="text-sm font-medium whitespace-nowrap">
                  {profile?.first_name || 'Account'}
                </span>
              </Link>
            ) : (
              <Link
                to="/login"
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium whitespace-nowrap"
              >
                Sign In
              </Link>
            )}
          </div>

          {/* CTA Buttons */}
          <div className="flex items-center space-x-3">
            <a
              href="https://wa.me/639178850200"
              target="_blank"
              rel="noopener noreferrer"
              className={`hidden lg:flex items-center justify-center w-10 h-10 rounded-lg transition-all cursor-pointer ${
                isScrolled
                  ? 'text-green-600 hover:bg-green-50'
                  : 'text-white hover:bg-white/10'
              }`}
              aria-label="Contact via WhatsApp"
            >
              <i className="ri-whatsapp-line text-xl"></i>
            </a>
            <Link
              to="/contact"
              className="btn btn-primary btn-sm"
            >
              Request Quote
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 rounded-lg cursor-pointer transition-colors ${
                isScrolled ? 'text-navy-900 hover:bg-gray-100' : 'text-white hover:bg-white/10'
              }`}
              aria-label="Toggle menu"
            >
              <i
                className={`${
                  isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
                } text-2xl`}
              ></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-3 text-sm font-medium rounded-lg cursor-pointer transition-colors ${
                  location.pathname === link.path
                    ? 'bg-primary-50 text-primary-600'
                    : 'text-navy-700 hover:bg-gray-50'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <a
              href="https://wa.me/639178850200"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center px-4 py-3 text-green-600 font-medium cursor-pointer hover:bg-green-50 rounded-lg transition-colors"
            >
              <i className="ri-whatsapp-line mr-2 text-lg"></i>
              WhatsApp
            </a>
            <Link
              to="/contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full btn btn-primary text-center"
            >
              Request Quote
            </Link>
            {user ? (
              <Link
                to="/account"
                className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="ri-user-line text-lg"></i>
                <span className="text-sm font-medium">
                  {profile?.first_name || 'My Account'}
                </span>
              </Link>
            ) : (
              <Link
                to="/login"
                className="block px-4 py-2 bg-teal-600 text-white text-center rounded-lg hover:bg-teal-700 text-sm font-medium whitespace-nowrap"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
